<template>
	<VerticalLayout>
		<Descriptions
			v-if="details"
			:column="1"
			bordered
			size="small"
			:labelStyle="{ backgroundColor: 'white' }"
			:contentStyle="{ backgroundColor: 'white' }"
		>
			<DescriptionsItem :label="$t('insights.roverPerformanceTest.details.status')">
				<StatusDisplay :source="details" field="status" prefix="insights.roverPerformanceTest.status" :badgeDict="statusBadgeDict" />
			</DescriptionsItem>
			<DescriptionsItem :label="$t('insights.roverPerformanceTest.details.statusOnline')">
				<StatusDisplay :source="statusOnline" prefix="insights.roverPerformanceTest.statusOnline" :badgeDict="statusOnlineBadgeDict" />
			</DescriptionsItem>
			<DescriptionsItem :label="$t('insights.roverPerformanceTest.details.started')">
				<DateTimeDisplay v-if="details != null" :date="details.started" />
			</DescriptionsItem>
		</Descriptions>
		<DividerAutoDirection>{{ $t('insights.roverPerformanceTest.settingsDivider') }}</DividerAutoDirection>
		<Form
			:model="formState"
			:label-col="{ span: 12, lg: { span: 8 } }"
			:wrapper-col="{ span: 12, lg: { span: 16 } }"
			:colon="false"
		>
			<FormItem :label="$t('insights.roverPerformanceTest.details.mountpoint')" v-bind="validateInfos.mountpoint">
				<Select v-model:value="formState.mountpoint" :disabled="isFormDisabled" :options="mountpointOptions" />
			</FormItem>
			<FormItem :label="$t('insights.roverPerformanceTest.details.mode')">
				<RadioGroup v-model:value="formState.mode" :disabled="isFormDisabled" :options="modeOptions" option-type="button" />
			</FormItem>
			<FormItem :label="$t('insights.roverPerformanceTest.details.points.' + formState.mode)" v-bind="validateInfos.points">
				<PointsListInput v-model="formState.points" :disabled="isFormDisabled" />
			</FormItem>
			<FormItem :label="$t('insights.roverPerformanceTest.details.altitude')" v-bind="validateInfos.altitude">
				<InputDecimal v-model="formState.altitude" :disabled="isFormDisabled" :allowPoint="false" range="non-negative" />
			</FormItem>
			<FormItem :label="$t('insights.roverPerformanceTest.details.startDelaySec')" v-bind="validateInfos.startDelaySec">
				<InputDecimal v-model="formState.startDelaySec" :disabled="isFormDisabled" :allowPoint="true" range="non-negative" />
			</FormItem>
			<FormItem v-if="formState.mode === 'area'" :label="$t('insights.roverPerformanceTest.details.stepLatKm')" v-bind="validateInfos.stepLatKm">
				<InputDecimal v-model="formState.stepLatKm" :disabled="isFormDisabled" :allowPoint="true" range="positive" />
			</FormItem>
			<FormItem v-if="formState.mode === 'area'" :label="$t('insights.roverPerformanceTest.details.stepLonKm')" v-bind="validateInfos.stepLonKm">
				<InputDecimal v-model="formState.stepLonKm" :disabled="isFormDisabled" :allowPoint="true" range="positive" />
			</FormItem>
			<FormItem v-if="formState.mode === 'area'" v-bind="validateInfos.maxRoversAmount">
				<template #label>
					<Tooltip placement="topLeft" color="black">
						<template #title>{{ $t('insights.roverPerformanceTest.details.maxRoversAmountHint') }}</template>
						<QuestionCircleOutlined /> {{ $t('insights.roverPerformanceTest.details.maxRoversAmount') }}
					</Tooltip>
				</template>
				<InputDecimal v-model="formState.maxRoversAmount" :disabled="isFormDisabled" :allowPoint="false" range="non-negative" />
			</FormItem>
			<FormItem v-if="formState.mode === 'route'" :label="$t('insights.roverPerformanceTest.details.roversAmount')" v-bind="validateInfos.roversAmount">
				<InputDecimal v-model="formState.roversAmount" :disabled="isFormDisabled" :allowPoint="false" range="positive" />
			</FormItem>
			<FormItem v-if="formState.mode === 'route'" :label="$t('insights.roverPerformanceTest.details.roversVelocityKph')" v-bind="validateInfos.roversVelocityKph">
				<InputDecimal v-model="formState.roversVelocityKph" :disabled="isFormDisabled" :allowPoint="false" range="positive" />
			</FormItem>
		</Form>
		<FormItem :wrapper-col="{ span: 24, offset: 0, sm: { span: 12, offset: 12 }, lg: { span: 16, offset: 8 } }">
			<Button v-if="showStartButton" type="primary" @click="onStartButtonClick" :loading="isSubmitting" :disabled="isLoading || isSubmitting || isUpdating">
				{{ $t('insights.roverPerformanceTest.buttonLabel.start') }}
			</Button>
			<Button v-else type="primary" @click="onStopButtonClick" :loading="isSubmitting" :disabled="isLoading || isSubmitting || isUpdating">
				{{ $t('insights.roverPerformanceTest.buttonLabel.stop') }}
			</Button>
		</FormItem>
	</VerticalLayout>
</template>

<script>
import { ref, computed, onMounted, reactive, toRaw, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Button, Descriptions, DescriptionsItem, Form, FormItem, Select, RadioGroup, Tooltip, message } from 'ant-design-vue';
import { QuestionCircleOutlined } from '@ant-design/icons-vue';

import Api from '@/api/Api';
import Utils from '@/api/Utils';
import Validators from '@/api/Validators';

import VerticalLayout from '@/components/common/VerticalLayout';
import DateTimeDisplay from '@/components/common/DateTimeDisplay';
import DividerAutoDirection from '@/components/common/DividerAutoDirection';
import InputDecimal from '@/components/common/InputDecimal.vue';
import PointsListInput from '@/components/common/PointsListInput.vue';
import StatusDisplay from '@/components/common/StatusDisplay.vue';

import { showErrorMessage } from '@/components/common/errorMessage';

import { useInterval } from '@/utils/useInterval';


export default {
	components: {
		Button, Descriptions, DescriptionsItem, Form, FormItem, Select, RadioGroup, Tooltip,
		DateTimeDisplay, VerticalLayout, DividerAutoDirection, InputDecimal, PointsListInput, StatusDisplay,
		QuestionCircleOutlined,
	},
	setup() {
		const { t } = useI18n();
		const isLoading = ref(true);
		const isSubmitting = ref(false);
		const details = ref({
			status: 'stopped',
			status_online: 'no_data',
		});
		const mountpointOptions = ref([]);
		const formState = reactive({
			mountpoint: '',
			mode: 'area',
			points: [],
			altitude: 0,
			startDelaySec: 0,
			stepLatKm: 0,
			stepLonKm: 0,
			maxRoversAmount: 0,
			roversAmount: 0,
			roversVelocityKph: 0,
		});
		const modeOptions = computed(() => ['area', 'route'].map(mode => ({
			value: mode,
			label: t('insights.roverPerformanceTest.mode.' + mode),
		})));

		const showStartButton = computed(() => {
			return details.value == null || (details.value.status === 'stopped' && !details.value.updating);
		});
		const { validate, validateInfos } = Validators.useRoverPerformanceTestFormRules(formState);
		const onStartButtonClick = () => {
			validate().then(() => {
				isSubmitting.value = true;
				const data = {
					...toRaw(formState),
					altitude: Utils.m2mm(formState.altitude),
					stepLatKm: Utils.km2m(formState.stepLatKm),
					stepLonKm: Utils.km2m(formState.stepLonKm),
				};
				Api.roversImitationStart(data, response => {
					isSubmitting.value = false;
					if (response.status === 'ok') {
						message.success(t('insights.roverPerformanceTest.messages.started'));
						loadTestDetails(true);
					} else {
						showErrorMessage(response.errors);
					}
				});
			}).catch(err => {
				console.log('validation error', err);
			});
		};

		const onStopButtonClick = () => {
			isSubmitting.value = true;
			Api.roversImitationStop(response => {
				isSubmitting.value = false;
				if (response.status === 'ok') {
					message.success(t('insights.roverPerformanceTest.messages.stopped'));
					loadTestDetails(true);
				} else {
					showErrorMessage(response.errors);
				}
			});
		};

		const statusBadgeDict = ref({
			'working': 'success',
		});

		const statusOnline = computed(() => {
			if (details.value == null) {
				return 'no_data';
			}
			if (details.value.updating) {
				return 'updating';
			}
			return details.value.status_online;
		});

		const statusOnlineBadgeDict = ref({
			'working': 'success',
			'updating': 'processing',
			'done': 'success',
			'failed': 'error',
		});

		const loadTestDetails = showLoading => {
			isLoading.value = showLoading;
			Api.roversImitationGetDetails(response => {
				if (response.status === 'ok') {
					details.value = response.details;
					formState.mountpoint = response.details.mountpoint;
					formState.mode = response.details.mode;
					formState.points = response.details.points;
					formState.altitude = Utils.mm2m(response.details.altitude);
					formState.startDelaySec = response.details.start_delay_sec;
					formState.stepLatKm = Utils.m2km(response.details.step_lat_km);
					formState.stepLonKm = Utils.m2km(response.details.step_lon_km);
					formState.maxRoversAmount = response.details.max_rovers_amount;
					formState.roversAmount = response.details.rovers_amount;
					formState.roversVelocityKph = response.details.rovers_velocity_kph;
				} else {
					showErrorMessage(response.errors);
				}
				isLoading.value = false;
			});
		};

		onMounted(() => {
			Api.vrsSettingsGet(responseData => {
				let mountpoints = responseData.subNetworksSettings
					.map(subNetwork => subNetwork.settings)
					.filter(snSetting => !snSetting.deleted)
					.flatMap(snSetting => snSetting.rtcmf_mountpoints.map(mountpoint =>
						snSetting.final_name_vrs + '_' + mountpoint.label
					));
				mountpoints.sort();
				mountpointOptions.value = mountpoints.map(mountpoint => ({
					value: mountpoint,
					label: mountpoint,
				}));
			});
		});

		const { isRunning } = useInterval(() => {
			loadTestDetails(false);
		}, 5000, true);
		watch(details, () => {
			isRunning.value = details.value == null || details.value.updating || details.value.status === 'working';
		});

		const isFormDisabled = computed(() => {
			return isLoading.value || isSubmitting.value || !showStartButton.value;
		});

		const isUpdating = computed(() => {
			return details.value == null || details.value.updating;
		});

		return {
			isLoading,
			isSubmitting,
			details,
			mountpointOptions,
			modeOptions,
			formState,
			isFormDisabled,
			statusBadgeDict,
			statusOnline,
			statusOnlineBadgeDict,
			showStartButton,
			onStartButtonClick,
			onStopButtonClick,
			validateInfos,
			isUpdating,
		};
	},
}
</script>
